import { ClientAllocationRow, ClientCapitalFlowRow, ClientMonthEntryInput } from "_graphql-types";

// ---- months: the API takes and returns GraphQLDate "YYYY-MM-DD"; the pickers use "YYYY-MM" ----

/** The month before `today`, as "YYYY-MM" (the legacy default). */
export function previousMonth(today: Date = new Date()): string {
  const d = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/** "YYYY-MM" -> the GraphQLDate of the month's first day. */
export function monthArg(month: string): string {
  if (!/^\d{4}-\d{2}$/.test(month)) throw new Error(`Not a month: ${month}`);
  return `${month}-01`;
}

/** A date-only API value ("YYYY-MM-DD", UTC midnight) as "Mar 2026", formatted in UTC. */
export function formatMonth(date: string): string {
  return new Date(`${date.slice(0, 10)}T00:00:00Z`).toLocaleDateString("en-US", {
    timeZone: "UTC",
    year: "numeric",
    month: "short",
  });
}

// ---- numbers ----------------------------------------------------------------------------------

const money = new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const formatMoney = (v: number | null | undefined) => (v === null || v === undefined ? "" : money.format(v));

/** A value already in percent units (1.25 = 1.25%). */
export const formatPercent = (v: number | null | undefined, digits = 2) =>
  v === null || v === undefined ? "" : `${v.toFixed(digits)}%`;

/** What an input shows for a stored number. */
export const toText = (v: number | null | undefined) => (v === null || v === undefined ? "" : String(v));

/** An input's text as a number: blank is null; anything else must be a number. */
export function parseNumber(text: string, label: string): number | null {
  const t = text.trim().replace(/,/g, "");
  if (t === "") return null;
  const n = Number(t);
  if (!Number.isFinite(n)) throw new Error(`${label}: "${text}" is not a number`);
  return n;
}

/** Sum of each key over rows, nulls as zero. */
export function totals<K extends string>(rows: Array<Partial<Record<K, number | null>>>, keys: readonly K[]) {
  const out = {} as Record<K, number>;
  for (const k of keys) out[k] = rows.reduce<number>((s, r) => s + (r[k] ?? 0), 0);
  return out;
}

/** The fields that are empty (null or zero) on every row: report columns not worth showing. */
export function emptyFields<R, K extends keyof R>(rows: readonly R[], fields: readonly K[]): Set<K> {
  return new Set(fields.filter(f => rows.every(r => r[f] === null || r[f] === undefined || r[f] === 0)));
}

/** A grid row's typed text differs from what it was loaded with. */
export const isDraftChanged = <K extends string>(draft: Record<K, string>, initial: Record<K, string>) =>
  (Object.keys(initial) as K[]).some(k => draft[k].trim() !== initial[k]);

// ---- Actual EndBal and Fee Entry ---------------------------------------------------------------

export const MONTH_ENTRY_FIELDS = [
  "actualEndBal",
  "mgmtFee",
  "adminFee",
  "incentiveFee",
  "finalGrossReturn",
  "finalNetReturn",
] as const;
export type MonthEntryField = (typeof MONTH_ENTRY_FIELDS)[number];
export type MonthEntryDraft = Record<MonthEntryField, string>;

/** The fields a derived share class may still enter. */
const DERIVED_FIELDS: readonly MonthEntryField[] = ["finalGrossReturn", "finalNetReturn"];

const FIELD_LABEL: Record<MonthEntryField, string> = {
  actualEndBal: "Actual net end balance",
  mgmtFee: "Mgmt fee",
  adminFee: "Admin fee",
  incentiveFee: "Perf fee",
  finalGrossReturn: "Final gross return",
  finalNetReturn: "Final net return",
};

type MonthEntryRow = Pick<
  ClientAllocationRow,
  | "shareClassId"
  | "shareClassName"
  | "derived"
  | "adjBegBal"
  | "actNetEndBal"
  | "mgmtFee"
  | "adminFee"
  | "incentiveFee"
  | "enteredMgmtFee"
  | "enteredAdminFee"
  | "enteredIncentiveFee"
  | "finalGrossReturn"
  | "finalNetReturn"
  | "actGrossReturn"
  | "actNetReturn"
>;

/** What the grid's inputs start as: the entered values (fees as entered, not as computed). */
export function monthEntryInitial(row: MonthEntryRow): MonthEntryDraft {
  return {
    actualEndBal: toText(row.actNetEndBal),
    mgmtFee: toText(row.enteredMgmtFee),
    adminFee: toText(row.enteredAdminFee),
    incentiveFee: toText(row.enteredIncentiveFee),
    finalGrossReturn: toText(row.finalGrossReturn),
    finalNetReturn: toText(row.finalNetReturn),
  };
}

export const isEditableMonthField = (row: Pick<MonthEntryRow, "derived">, field: MonthEntryField) =>
  !row.derived || DERIVED_FIELDS.includes(field);

function parseMonthField(field: MonthEntryField, text: string, shareClassName: string): number | null {
  const v = parseNumber(text, `${shareClassName} ${FIELD_LABEL[field]}`);
  if (v === null) return null;
  if (field === "actualEndBal" && v < 0) throw new Error(`${shareClassName}: the actual net end balance must be zero or more`);
  if ((field === "finalGrossReturn" || field === "finalNetReturn") && (v < -100 || v > 100)) {
    throw new Error(`${shareClassName}: ${FIELD_LABEL[field]} must be a percent between -100 and 100`);
  }
  return v;
}

/**
 * The saveClientMonthEntries input: only rows with a change, and in each only the fields that
 * changed (an omitted field is left as it is; a cleared one is sent as null). A derived share class
 * sends only its final returns. Throws on any invalid input.
 */
export function monthEntryInputs(rows: MonthEntryRow[], drafts: ReadonlyMap<number, MonthEntryDraft>): ClientMonthEntryInput[] {
  const out: ClientMonthEntryInput[] = [];
  for (const row of rows) {
    const draft = drafts.get(row.shareClassId);
    if (!draft) continue;
    const initial = monthEntryInitial(row);
    const entry: ClientMonthEntryInput = { shareClassId: row.shareClassId };
    let changed = false;
    for (const field of MONTH_ENTRY_FIELDS) {
      if (!isEditableMonthField(row, field) || draft[field].trim() === initial[field]) continue;
      entry[field] = parseMonthField(field, draft[field], row.shareClassName);
      changed = true;
    }
    if (changed) out.push(entry);
  }
  return out;
}

/**
 * The actual gross and net returns (percent) for what is typed, computed as the server does:
 * net = (actNetEndBal - adjBegBal) / adjBegBal; gross adds back the mgmt, admin and perf fees.
 * An untouched row shows the server's values. A blank fee is the calculated fee, which is only
 * known here when no fee was entered before; a cleared entered fee leaves gross unknown (null)
 * until saved.
 */
export function liveReturns(row: MonthEntryRow, draft: MonthEntryDraft): { gross: number | null; net: number | null } {
  const initial = monthEntryInitial(row);
  if (MONTH_ENTRY_FIELDS.every(f => draft[f].trim() === initial[f])) {
    return { gross: row.actGrossReturn ?? null, net: row.actNetReturn ?? null };
  }
  const safe = (text: string) => {
    try {
      return parseNumber(text, "");
    } catch {
      return undefined; // invalid while typing: no live figure; Save reports it
    }
  };
  const end = safe(draft.actualEndBal);
  const adj = row.adjBegBal ?? 0;
  if (end === null || end === undefined || adj === 0) return { gross: null, net: null };
  const net = ((end - adj) / adj) * 100;

  const fee = (text: string, entered: number | null | undefined, computed: number | null | undefined) => {
    const v = safe(text);
    if (v !== null) return v; // a typed fee, or undefined if invalid
    return entered === null || entered === undefined ? (computed ?? 0) : undefined;
  };
  const fees = [
    fee(draft.mgmtFee, row.enteredMgmtFee, row.mgmtFee),
    fee(draft.adminFee, row.enteredAdminFee, row.adminFee),
    fee(draft.incentiveFee, row.enteredIncentiveFee, row.incentiveFee),
  ];
  if (fees.some(f => f === undefined)) return { gross: null, net };
  const gross = ((end + (fees as number[]).reduce((s, f) => s + f, 0) - adj) / adj) * 100;
  return { gross, net };
}

// ---- Capital Flow Report -----------------------------------------------------------------------

export const FLOW_KEYS = ["addition", "redemption", "switchIn", "switchOut"] as const;
export type FlowKey = (typeof FLOW_KEYS)[number];

export interface CapitalFlowReportRow {
  id: string;
  /** null on the totals row. */
  date: string | null;
  /** By `${shareClassId}.${FlowKey}`. */
  values: Record<string, number | null>;
}

export const flowCell = (shareClassId: number, key: FlowKey) => `${shareClassId}.${key}`;

/** One row per month, newest first, then a totals row; columns per share class and flow. */
export function capitalFlowReport(flows: ClientCapitalFlowRow[]): CapitalFlowReportRow[] {
  const byDate = new Map<string, CapitalFlowReportRow>();
  const total: CapitalFlowReportRow = { id: "total", date: null, values: {} };
  for (const f of flows) {
    const row = byDate.get(f.date) ?? { id: f.date, date: f.date, values: {} };
    byDate.set(f.date, row);
    for (const k of FLOW_KEYS) {
      const v = f[k] ?? null;
      const cell = flowCell(f.shareClassId, k);
      row.values[cell] = v === null ? (row.values[cell] ?? null) : (row.values[cell] ?? 0) + v;
      total.values[cell] = (total.values[cell] ?? 0) + (v ?? 0);
    }
  }
  // A month whose only rows are zero (a cleared flow the legacy app kept) shows nothing; drop it.
  const rows = [...byDate.values()]
    .filter(r => Object.values(r.values).some(v => v !== null && v !== 0))
    .sort((a, b) => (b.date as string).localeCompare(a.date as string));
  return rows.length ? [...rows, total] : [];
}
